<template>
  <header 
    :class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled ? 'navbar-blur shadow-lg' : 'bg-transparent'
    ]"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center py-4">
        <!-- Logo -->
        <div class="flex items-center space-x-2">
          <img
            :src="isDark ? '/FORZATI impreblanco .png' : '/FORZATIweb.png'"
            alt="Forza TI"
            class="h-20 w-auto"
          />
        </div>

        <!-- Desktop Navigation -->
        <nav class="hidden md:flex items-center space-x-8">
          <button
            v-for="item in navigation"
            :key="item.name"
            @click="scrollToSection(item.href)"
            class="text-foreground hover:text-primary transition-colors duration-300 font-medium"
          >
            {{ item.name }}
          </button>
          
          <!-- Theme Toggle -->
          <button
            @click="toggleTheme"
            class="ml-4 p-2 rounded-md hover:bg-accent transition-colors"
          >
            <Sun v-if="isDark" class="h-5 w-5" />
            <Moon v-else class="h-5 w-5" />
          </button>
        </nav>

        <!-- Mobile menu button -->
        <div class="md:hidden flex items-center space-x-1">
          <button
            @click="toggleTheme"
            class="h-8 w-8 p-1 rounded-md hover:bg-accent transition-colors"
          >
            <Sun v-if="isDark" class="h-4 w-4" />
            <Moon v-else class="h-4 w-4" />
          </button>
          
          <button
            @click="isMenuOpen = !isMenuOpen"
            class="h-8 w-8 p-1 rounded-md hover:bg-accent transition-colors"
          >
            <X v-if="isMenuOpen" class="h-5 w-5" />
            <Menu v-else class="h-5 w-5" />
          </button>
        </div>
      </div>

      <!-- Mobile Navigation -->
      <div v-if="isMenuOpen" class="md:hidden">
        <div class="px-2 pt-2 pb-3 space-y-1 bg-background-secondary/90 backdrop-blur-md rounded-lg mt-2">
          <button
            v-for="item in navigation"
            :key="item.name"
            @click="scrollToSection(item.href)"
            class="block w-full text-left px-3 py-2 text-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-colors duration-300"
          >
            {{ item.name }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { Menu, X, Sun, Moon } from 'lucide-vue-next'

const isMenuOpen = ref(false)
const isScrolled = ref(false)
const isDark = ref(false)

const navigation = [
  { name: "Inicio", href: "#inicio" },
  { name: "Servicios", href: "#servicios" },
  { name: "Nosotros", href: "#nosotros" },
  { name: "Casos de Éxito", href: "#casos" },
  { name: "Contacto", href: "#contacto" },
]

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50
}

const scrollToSection = (href: string) => {
  const element = document.querySelector(href)
  if (element) {
    element.scrollIntoView({ behavior: "smooth" })
    isMenuOpen.value = false
  }
}

const toggleTheme = () => {
  isDark.value = !isDark.value
  document.documentElement.classList.toggle('light', !isDark.value)
  localStorage.setItem('forza-ui-theme', isDark.value ? 'dark' : 'light')
}

onMounted(() => {
  // Initialize theme - default to light
  const savedTheme = localStorage.getItem('forza-ui-theme')
  if (savedTheme === 'dark') {
    isDark.value = true
    document.documentElement.classList.remove('light')
  } else {
    isDark.value = false
    document.documentElement.classList.add('light')
  }

  window.addEventListener("scroll", handleScroll)
})

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll)
})
</script>